<template>
  <div :class="['filters-container', !expandedFilters ? 'unexpanded-container' : '']">
    <SelectList
      v-for="field in filtersConfig"
      :key="field.id"
      :fieldConfig="field"
      :values="values"
      @change="updateFilter"
      :setExpandedFilters="setExpandedFilters"
      :expandedFilters="expandedFilters"
    />
    <ProductAndCountryFilter />
    <TacticContextFilter />
    <BbBtn
      @click="clearFilters"
      v-if="!disabledStartButton"
      :class="['cancel-button', !expandedFilters ? 'button-unexpanded-filters' : '']"
    >
      <TextBody i18n-key="common.cancel"></TextBody>
    </BbBtn>
    <BbBtn
      @click="startAuthoringConversation"
      :is-disabled="disabledStartButton"
      :class="['filter-button', !expandedFilters ? 'button-unexpanded-filters' : '']"
    >
      <TextBody i18n-key="common.start"></TextBody>
    </BbBtn>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n/dist/vue-i18n';

import useCollections from '~/composables/useCollections';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useConversations from '~/composables/useConversations';
import useAuthoring from '~/composables/useAuthoring';

import { SetCurrentConversation } from '~/constants/mutations';
import { SetSelectedValues } from '~/constants/actions';

import { calculatePassedTime } from '~/utils/date-utils';
import { queryType } from '~/constants/conversationModels';

import SelectList from '~/components/common/SelectList.vue';
import TextBody from '~/components/common/TextBody';
import TacticContextFilter from '~/components/conversation/authoring/tacticsContexts/TacticContextFilter';
import ProductAndCountryFilter from '~/components/conversation/authoring/productAndCountry/ProductAndCountryFilter.vue';

export default {
  components: { TacticContextFilter, SelectList, TextBody, ProductAndCountryFilter },
  emits: ['unsubscribeConversation'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const { currentCollections } = useCollections();
    const { currentTemplate, currentTemplates, getTemplateById, getDesignSystemById } = useDocumentEditor();
    const { isAuthoringStarted, setIsAuthoringStarted, expandedFilters, setExpandedFilters, selectedValues } =
      useAuthoring();
    const {
      createAuthoringConversation,
      currentConversation,
      publishToConversation,
      setConversationScope,
      setDefaultState,
    } = useConversations();

    const filtersList = ref([
      {
        id: 'design',
        optionList: [],
        name: t('author.filters.designSystem'),
        placeholder: t('author.filters.pickDesignSystem'),
      },
      {
        id: 'collection',
        optionList: [],
        name: t('author.filters.collection'),
        placeholder: t('author.filters.pickCollection'),
      },
    ]);
    const updateFilter = async ({ value, id }) => {
      if (id && selectedValues.value[id] !== value) {
        await store.dispatch(`authoringStore/${SetSelectedValues}`, { id, value });
        // set collection to use it at publish message logic
        // ai needs this collection data for summerizing it
        if (id === 'collection') {
          const currentCollection = currentCollections.value.find((collection) => collection.collectionId === value);
          setConversationScope({ collection: currentCollection });
          // when changing the collection an empty message together with the new collection should be published to LLM
          if (isAuthoringStarted.value && currentConversation.value.conversationId) {
            await publishToConversation(currentConversation.value.conversationId, queryType.authoringQuery, '');
            // usually filters are expanded until you click start, but because we don't need to press start after choosing a different collection, when isAuthoringStarted we'll collapse the filters directly
            setExpandedFilters(
              isAuthoringStarted &&
                !!Object.keys(selectedValues.value).find((item) => selectedValues.value[item] === null),
            );
          }
        } else if (id === 'design') {
          setIsAuthoringStarted(false);
          // setSelectedAuthoredDocument(null);
          store.dispatch(`conversationStore/${SetCurrentConversation}`, {
            ...currentConversation.value,
            designSystemId: null,
            templateId: null,
          });

          // reload the document based on what user chooses
          await getTemplateById(value);
        }
      }
    };

    const clearFilters = () => {
      setDefaultState();
      // selectedValues.value = { collection: null, design: null };
      store.dispatch(`authoringStore/${SetSelectedValues}`, { id: 'collection', value: null });
      store.dispatch(`authoringStore/${SetSelectedValues}`, { id: 'design', value: null });
    };

    const isButtonDisabled = computed(
      () => !!Object.keys(selectedValues.value).find((item) => selectedValues.value[item] === null),
    );

    const startAuthoringConversation = async () => {
      emit('unsubscribeConversation');
      const { content = '' } = currentTemplate?.value;
      await createAuthoringConversation(content);
      setIsAuthoringStarted(true);
      setExpandedFilters(false);
      const { parentId = '' } = currentTemplate?.value;
      if (parentId) {
        await getDesignSystemById(parentId);
      }
    };

    watch(currentCollections, (newValue) => {
      const collectionsList = newValue
        .filter((item) => item.documents.length)
        .map((item) => {
          const passedTime = calculatePassedTime(item.modifiedAt);

          return {
            label: item.collectionName,
            subtitle: `${item.documents.length} documents • Updated ${passedTime}`,
            value: item.collectionId,
          };
        });

      filtersList.value.find((item) => item.id === 'collection').optionList = collectionsList;
    });

    watch(currentTemplates, (newValue) => {
      const designSystemsList = newValue.map((item) => {
        const passedTime = calculatePassedTime(item.createdAt);

        return {
          label: item.parentName,
          subtitle: `${item.templateName} • Updated ${passedTime}`,
          value: item.id,
          isDeprecated: item.isDeprecated,
        };
      });

      filtersList.value.find((item) => item.id === 'design').optionList = designSystemsList;
    });

    return {
      filtersConfig: filtersList,
      values: selectedValues,
      isAuthoringStarted,
      updateFilter,
      disabledStartButton: isButtonDisabled,
      clearFilters,
      startAuthoringConversation,
      expandedFilters,
      setExpandedFilters,
    };
  },
};
</script>

<style lang="scss" scoped>
.filters-container {
  display: flex;
  flex: 1 1 0.2;
  border-radius: 50px;
  background: $bb-neutral-5;
  border: 1px solid $bb-neutral-4;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .filter-button {
    height: 60px !important;
    background: $bb-primary-3 !important;
    width: 126px !important;
    color: $bb-neutral-0 !important;
    margin: 0 4px;
  }

  :deep(.bb-btn.v-btn--disabled) {
    background: $bb-neutral-3 !important;
  }

  .cancel-button {
    height: 60px !important;
    background: #1a1a1a !important;
    width: 126px !important;
    color: $bb-neutral-0 !important;
    margin: 0 4px;
  }

  .button-unexpanded-filters {
    height: 28px !important;
  }
}

.unexpanded-container {
  height: 36px !important;
  background: $bb-neutral-0 !important;
}
</style>
